/**
 * Generates a deterministic avatar background using static glass texture with dynamic colors.
 * Replaces DiceBear dependency for security reasons while maintaining
 * consistent color assignment and beautiful glass texture per user.
 *
 * @param seed - A unique identifier for the user (typically user ID or email)
 * @returns Object with color and SVG data URL for the textured background
 *
 * @example
 * ```typescript
 * // Generate textured background for a user
 * const { color, svgDataUrl } = getAvatarBackground("user-123");
 * // Returns: { color: "#1e3a8a", svgDataUrl: "data:image/svg+xml;base64,..." }
 * ```
 */

const GLASS_TEXTURE_SVG_TEMPLATE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" fill="none" shape-rendering="auto">
  <mask id="viewboxMask"><rect width="100" height="100" rx="0" ry="0" x="0" y="0" fill="#fff" /></mask>
  <g mask="url(#viewboxMask)">
    <rect fill="{{COLOR}}" width="100" height="100" x="0" y="0" />
    <g style="mix-blend-mode:screen" opacity="0.6" filter="url(#glass-a)">
      <g transform="translate(-25 -25)">
        <g transform="translate(14, -47) rotate(130 75 75)">
          <path d="M94.8 3.2v145.5H55.3V3.2z" fill="white"/>
        </g>
      </g>
    </g>
    <g style="mix-blend-mode:screen" opacity="0.6" filter="url(#glass-b)">
      <g transform="translate(-25 -25)">
        <g transform="translate(-18, 50) rotate(-103 75 75)">
          <path d="M101.6 51.1a20 20 0 0 0-14.7-14.5q-3.9-1-8.5-1.1-10 0-17 4.8A30 30 0 0 0 50.9 54q-3.6 9-3.6 21.6 0 12.8 3.5 21.8 3.4 9 10.3 14a29 29 0 0 0 17.2 4.8q9 0 15-2.6 6-2.7 9-7.5t3-11.4l6.8.7H78.7v-28h64.5v20q0 20-8.4 34.1a56 56 0 0 1-23.2 21.6Q97 150.6 78 150.6q-21.3 0-37.3-9t-25-25.8-9-39.7a87 87 0 0 1 5.4-32 67 67 0 0 1 38-38Q63 1 77.6 1q12.9 0 24 3.7 11.1 3.6 19.7 10.4a50 50 0 0 1 20.2 36z" fill="white"/>
        </g>
      </g>
    </g>
    <defs>
      <filter id="glass-a" x="-57" y="-57" width="214" height="214" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
        <feGaussianBlur stdDeviation="16" result="effect1_foregroundBlur_25_7"/>
      </filter>
      <filter id="glass-b" x="-57" y="-57" width="214" height="214" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
        <feGaussianBlur stdDeviation="16" result="effect1_foregroundBlur_25_7"/>
      </filter>
    </defs>
  </g>
</svg>`;

const AVATAR_BACKGROUND_COLORS = [
  "#1a202c", // dark gray-blue
  "#2d3748", // slate gray
  "#4a5568", // gray
  "#1e3a8a", // blue-800
  "#1e40af", // blue-700
  "#1d4ed8", // blue-600
  "#374151", // gray-700
  "#273043", // dark purple-gray
  "#204D5A", // dark teal
  "#435D60", // muted teal-gray
  "#565438", // olive-brown
] as const;

const generateHash = (seed: string): number => {
  // Ensure we have a valid seed
  if (!seed || seed.trim() === "") {
    seed = "anonymous";
  }

  // Simple hash function to get deterministic index
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return hash;
};

export const getAvatarBackgroundColor = (seed: string): string => {
  const hash = generateHash(seed);
  const index = Math.abs(hash) % AVATAR_BACKGROUND_COLORS.length;
  return AVATAR_BACKGROUND_COLORS[index] ?? AVATAR_BACKGROUND_COLORS[0];
};

export const getAvatarBackground = (seed: string): { color: string; svgDataUrl: string } => {
  const color = getAvatarBackgroundColor(seed);

  // Inject the color into the SVG template
  const colorizedSvg = GLASS_TEXTURE_SVG_TEMPLATE.replace("{{COLOR}}", color);

  // Convert to data URL
  const svgDataUrl = `data:image/svg+xml;base64,${btoa(colorizedSvg)}`;

  return { color, svgDataUrl };
};
